/**
 * AugmentationDetailModal - Shows detailed info about a single augmentation
 * Reuses .chess-modal-overlay and .chess-modal CSS classes from the shared UI
 */

import type {
  Augmentation,
  AugmentationCategory,
} from "@cloudnest/augmented-chess-engine";
import { getPieceTypeDisplayName } from "@cloudnest/augmented-chess-engine";
import { getPieceSymbol } from "../../../chess-ui";

const CATEGORY_LABELS: Record<AugmentationCategory, string> = {
  MOVEMENT_MODIFIER: "\uC774\uB3D9 \uBCC0\uACBD",
  PHASE_MOVEMENT: "\uAD00\uD1B5 \uC774\uB3D9",
  PROMOTION_MODIFIER: "\uC2B9\uC9C4 \uBCC0\uACBD",
  MOVEMENT_SWAP: "\uD328\uD134 \uAD50\uD658",
  WIN_CONDITION_MODIFIER: "\uC2B9\uB9AC \uC870\uAC74",
  CAPTURE_MODIFIER: "\uD3EC\uD68D \uBCC0\uD615",
  RESOURCE: "\uC790\uC6D0",
  DEFENSE: "\uBC29\uC5B4",
};

interface AugmentationDetailModalProps {
  augmentation: Augmentation | null;
  onClose: () => void;
}

function getEffectDetailLines(augmentation: Augmentation): string[] {
  switch (augmentation.effect.type) {
    case "ADD_PATTERN":
      if (augmentation.effect.pattern === "KING_STEP") {
        return [
          "\uB300\uC0C1 \uAE30\uBB3C\uC774 \uD0B9\uCC98\uB7FC \uBAA8\uB4E0 \uBC29\uD5A5 1\uCE78 \uC774\uB3D9 \uD328\uD134\uC744 \uCD94\uAC00\uB85C \uD68D\uB4DD\uD569\uB2C8\uB2E4.",
        ];
      }
      if (augmentation.effect.pattern === "DIAGONAL_2") {
        return [
          "\uB300\uC0C1 \uAE30\uBB3C\uC774 \uB300\uAC01\uC120 2\uCE78 \uC774\uB3D9 \uD328\uD134\uC744 \uCD94\uAC00\uB85C \uD68D\uB4DD\uD569\uB2C8\uB2E4.",
        ];
      }
      if (augmentation.effect.pattern === "FORWARD_2") {
        return [
          "\uB300\uC0C1 \uD3F0\uC740 \uC2DC\uC791 \uC704\uCE58 \uC5EC\uBD80\uC640 \uAD00\uACC4\uC5C6\uC774 \uC804\uBC29 2\uCE78 \uC774\uB3D9 \uD328\uD134\uC744 \uC0AC\uC6A9\uD569\uB2C8\uB2E4.",
        ];
      }
      if (augmentation.effect.pattern === "ORTHOGONAL_2") {
        return [
          "\uB300\uC0C1 \uAE30\uBB3C\uC774 \uAC00\uB85C/\uC138\uB85C 2\uCE78 \uC774\uB3D9 \uD328\uD134\uC744 \uCD94\uAC00\uB85C \uD68D\uB4DD\uD569\uB2C8\uB2E4.",
        ];
      }
      return [
        "\uB300\uC0C1 \uAE30\uBB3C\uC758 \uC774\uB3D9 \uD328\uD134\uC774 \uD655\uC7A5\uB429\uB2C8\uB2E4.",
      ];
    case "EXTEND_RANGE":
      return [
        `\uC2AC\uB77C\uC774\uB529 \uAE30\uBB3C\uC758 \uC774\uB3D9 \uAC00\uB2A5 \uAC70\uB9AC\uAC00 ${augmentation.effect.rangeBonus ?? 0}\uCE78 \uC99D\uAC00\uD569\uB2C8\uB2E4.`,
      ];
    case "PHASE_MOVEMENT":
      return [
        "\uB300\uC0C1 \uAE30\uBB3C\uC740 \uC911\uAC04 \uAE30\uBB3C\uC744 \uD1B5\uACFC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.",
        "\uCC29\uC9C0 \uC870\uAC74\uC740 \uAE30\uBCF8 \uADDC\uCE59(\uBE48 \uCE78 \uB610\uB294 \uC0C1\uB300 \uAE30\uBB3C)\uACFC \uB3D9\uC77C\uD569\uB2C8\uB2E4.",
      ];
    case "PROMOTION_RANK_OVERRIDE":
      return [
        `\uBC31 \uD3F0\uC740 ${augmentation.effect.whiteRank}\uB7AD\uD06C, \uD751 \uD3F0\uC740 ${augmentation.effect.blackRank}\uB7AD\uD06C\uC5D0\uC11C\uB3C4 \uC2B9\uC9C4\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.`,
      ];
    case "MOVEMENT_SWAP":
      return (augmentation.effect.swapPairs ?? []).map(
        (pair) =>
          `${getPieceTypeDisplayName(pair.pieceA)} \u2194 ${getPieceTypeDisplayName(pair.pieceB)} \uC774\uB3D9 \uD328\uD134\uC744 \uC11C\uB85C \uAD50\uD658\uD569\uB2C8\uB2E4.`,
      );
    case "CONSECUTIVE_CHECK_WIN":
      return [
        `\uAC19\uC740 \uD50C\uB808\uC774\uC5B4\uAC00 \uC5F0\uC18D ${augmentation.effect.checkCount}\uD68C \uCCB4\uD06C\uD558\uBA74 \uC989\uC2DC \uC2B9\uB9AC\uD569\uB2C8\uB2E4.`,
        "\uC911\uAC04\uC5D0 \uCCB4\uD06C\uAC00 \uC544\uB2CC \uC218\uB97C \uB450\uBA74 \uC5F0\uC18D \uCCB4\uD06C \uCE74\uC6B4\uD2B8\uAC00 \uCD08\uAE30\uD654\uB429\uB2C8\uB2E4.",
      ];
    case "INITIAL_PLACEMENT":
      return [
        `\uC0C8\uB85C\uC6B4 \uAE30\uBB3C\uC774 \uBCF4\uB4DC\uC5D0 \uBC30\uCE58\uB429\uB2C8\uB2E4.`,
        `\uBC30\uCE58\uB41C \uAE30\uBB3C\uC740 \uC989\uC2DC \uAC8C\uC784\uC5D0 \uCC38\uC5EC\uD569\uB2C8\uB2E4.`,
      ];
    case "KING_ADJACENT_IMMUNITY":
      return [
        `\uD0B9 \uC8FC\uBCC0 ${augmentation.effect.radius}\uCE78 \uC774\uB0B4\uC758 \uC544\uAD70 \uAE30\uBB3C\uC740 \uC801\uC5D0\uAC8C \uC7A1\uD788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.`,
        "\uD0B9\uC758 \uBCF4\uD638 \uBC94\uC704\uC5D0 \uC788\uB294 \uAE30\uBB3C\uC740 \uBA74\uC5ED \uD6A8\uACFC\uB97C \uBC1B\uC2B5\uB2C8\uB2E4.",
      ];
    default:
      return [
        "\uAC8C\uC784 \uADDC\uCE59\uC5D0 \uC601\uD5A5\uC744 \uC8FC\uB294 \uD6A8\uACFC\uAC00 \uC801\uC6A9\uB429\uB2C8\uB2E4.",
      ];
  }
}

export function AugmentationDetailModal({
  augmentation,
  onClose,
}: AugmentationDetailModalProps): JSX.Element | null {
  if (!augmentation) return null;

  const categoryLabel = CATEGORY_LABELS[augmentation.category];
  const targetPieces = augmentation.targetPieceTypes.map((type) => ({
    type,
    symbol: getPieceSymbol("WHITE", type),
  }));
  const effectDetails = getEffectDetailLines(augmentation);

  return (
    <div
      className="chess-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={augmentation.nameKo ?? augmentation.name}
      tabIndex={-1}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="chess-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chess-modal-header">
          <h2>{augmentation.nameKo ?? augmentation.name}</h2>
          <p style={{ fontSize: "0.85rem", color: "var(--chess-text-muted)" }}>
            {categoryLabel}
          </p>
        </div>

        <p style={{ color: "var(--chess-text-secondary)", marginBottom: 16 }}>
          {augmentation.descriptionKo ?? augmentation.description}
        </p>

        {targetPieces.length > 0 && (
          <div
            style={{ fontSize: "0.85rem", color: "var(--chess-text-muted)" }}
          >
            <strong>{"\uB300\uC0C1 \uAE30\uBB3C:"}</strong>{" "}
            {targetPieces.map((p) => `${p.symbol} ${p.type}`).join(", ")}
          </div>
        )}

        <div
          style={{
            marginTop: 12,
            fontSize: "0.85rem",
            color: "var(--chess-text-secondary)",
          }}
        >
          <strong style={{ color: "var(--chess-text-primary)" }}>
            {"\uD6A8\uACFC \uC0C1\uC138:"}
          </strong>
          <ul style={{ marginTop: 6, paddingLeft: 18 }}>
            {effectDetails.map((line) => (
              <li key={line} style={{ marginBottom: 4 }}>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-md border-none bg-gray-600 px-5 py-2 font-medium text-sm text-white transition-colors hover:bg-gray-700"
          >
            {"\uB2EB\uAE30"}
          </button>
        </div>
      </div>
    </div>
  );
}
